import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Shield, BookOpen, TrendingUp, Search } from 'lucide-react';
import { useFilterParams } from '@/store/filtersStore';
import { fetchCumplimiento } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChartCard } from '@/components/dashboard/ChartCard';

export function CumplimientoPanel() {
  const globalFilters = useFilterParams();
  const [search, setSearch] = useState('');

  const params = {
    desde: globalFilters.desde,
    hasta: globalFilters.hasta,
    sede: globalFilters.sede,
    gerente: globalFilters.gerente,
    negocio: globalFilters.negocio,
  };

  const { data: personas = [], isLoading } = useQuery({
    queryKey: ['cumplimiento', params],
    queryFn: () => fetchCumplimiento(params),
    staleTime: 120000,
  });

  const filtered = search
    ? personas.filter(p => p.persona?.toLowerCase().includes(search.toLowerCase()))
    : personas;

  const totalOPS = personas.reduce((s, p) => s + p.ops, 0);
  const totalLid = personas.reduce((s, p) => s + p.liderazgo, 0);
  const totalIncum = personas.reduce((s, p) => s + p.incum_ops, 0);
  const totalPersonas = personas.length;

  return (
    <div className="space-y-4" data-testid="cumplimiento-panel">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Personas', value: totalPersonas, icon: Users, color: 'var(--vz-blue)' },
          { label: 'Total OPS', value: totalOPS, icon: Shield, color: 'var(--vz-green)' },
          { label: 'Total Liderazgo', value: totalLid, icon: BookOpen, color: '#AB47BC' },
          { label: 'Incumplimientos', value: totalIncum, icon: TrendingUp, color: 'var(--vz-red)' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div
            key={label}
            className="rounded-xl p-3 flex items-center gap-3"
            style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}
          >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
              <Icon size={14} style={{ color }} />
            </div>
            <div>
              <p className="font-montserrat font-bold text-base leading-tight" style={{ color: 'var(--vz-text-primary)' }}>
                {value.toLocaleString('es-ES')}
              </p>
              <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative flex items-center">
        <Search size={12} className="absolute left-3" style={{ color: 'var(--vz-text-muted)' }} />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar persona..."
          className="pl-8 h-8 text-xs w-56"
          style={{
            background: 'var(--vz-bg-elevated)',
            border: '1px solid var(--vz-border-subtle)',
            color: 'var(--vz-text-primary)',
          }}
          data-testid="cumplimiento-search"
        />
      </div>

      {/* Table */}
      <ScrollArea className="h-[380px]">
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              {['#', 'Persona', 'OPS', 'Liderazgo', 'Total', 'Incumpl. OPS', '% Incumpl.'].map((h, i) => (
                <TableHead
                  key={h}
                  className="text-[10px] uppercase tracking-wider font-montserrat sticky top-0"
                  style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)', textAlign: i >= 2 ? 'right' : 'left' }}
                >
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}><div className="h-3 rounded animate-pulse" style={{ background: 'var(--vz-bg-elevated)', width: '80%' }} /></TableCell>
                    ))}
                  </TableRow>
                ))
              : filtered.length === 0
                ? <TableRow><TableCell colSpan={7} className="text-center text-xs py-8" style={{ color: 'var(--vz-text-muted)' }}>Sin registros</TableCell></TableRow>
                : filtered.map((p, i) => {
                    const total = p.ops + p.liderazgo;
                    const pct = p.ops > 0 ? Math.round((p.incum_ops / p.ops) * 100) : 0;
                    return (
                      <TableRow
                        key={p.persona}
                        className="transition-colors duration-150"
                        style={{ borderColor: 'var(--vz-border-subtle)' }}
                        data-testid="cumplimiento-row"
                      >
                        <TableCell className="text-[10px] font-mono-custom w-8" style={{ color: 'var(--vz-text-muted)' }}>{i + 1}</TableCell>
                        <TableCell className="text-xs font-medium max-w-[180px] truncate" style={{ color: 'var(--vz-text-primary)' }}>
                          {p.persona}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono-custom" style={{ color: 'var(--vz-green)' }}>{p.ops}</TableCell>
                        <TableCell className="text-xs text-right font-mono-custom" style={{ color: '#AB47BC' }}>{p.liderazgo}</TableCell>
                        <TableCell className="text-xs text-right font-mono-custom font-semibold" style={{ color: 'var(--vz-text-primary)' }}>{total}</TableCell>
                        <TableCell className="text-right">
                          {p.incum_ops > 0 ? (
                            <Badge className="text-[10px]" style={{ background: 'rgba(198,40,40,0.12)', color: 'var(--vz-red)', border: '1px solid rgba(198,40,40,0.2)' }}>
                              {p.incum_ops}
                            </Badge>
                          ) : (
                            <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono-custom" style={{ color: pct > 20 ? 'var(--vz-red)' : pct > 10 ? 'var(--vz-orange)' : 'var(--vz-green)' }}>
                          {p.ops > 0 ? `${pct}%` : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })
            }
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}

export default CumplimientoPanel;
